/**
 * The Admin's mass broadcast, notification banners and driver award campaigns (W9-BROADCAST-BANNERS-AWARDS, D477, ADR-0095; DevMobility
 * H-014, H-015, H-016, H-018, H-019, I-106, F-051, F-052, I-053) — framework-neutral view-models.
 *
 *  - **Marketing › Envios em massa** (`broadcasts`): the audience (riders or drivers of a country, regions, markets, ride history), the
 *    channels (in-app, SMS, WhatsApp template; PUSH waits for the native track), the content, a PREVIEW of who is reached market by
 *    market under each market's governed policy, then the send and its delivery progress.
 *  - **Marketing › Banners** (`banners`): a text with a window, a user type and a scope; the app shows the most specific live one.
 *  - **Motoristas › Premiação** (`awards`): goal campaigns that credit the driver's wallet — one operator proposes, ANOTHER activates.
 *
 * The console only presents: the server authorizes every call at every market it touches. Neutral: no country, currency or language is
 * named here; money is integer minor units rendered through `@vello/format`.
 */
import type { AwardCampaignView, BannerScopeView, BroadcastCampaignView, BroadcastProgressView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

// ---- authority (display only — the server re-authorizes every call) ---------------------------------------------------

export function broadcastAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly maySend: boolean } {
  if (grants === undefined) return { mayView: false, maySend: false };
  return { mayView: grantsAllow(grants, { permission: 'broadcast:view', target: 'GEO' }), maySend: grantsAllow(grants, { permission: 'broadcast:send', target: 'GEO' }) };
}

export function bannerAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return { mayView: grantsAllow(grants, { permission: 'banner:view', target: 'GEO' }), mayManage: grantsAllow(grants, { permission: 'banner:manage', target: 'GEO' }) };
}

export function awardAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return { mayView: grantsAllow(grants, { permission: 'award:view', target: 'GEO' }), mayManage: grantsAllow(grants, { permission: 'award:manage', target: 'GEO' }) };
}

export type BroadcastTone = 'neutral' | 'success' | 'warning' | 'danger';

export function broadcastStatusTone(status: BroadcastCampaignView['status']): BroadcastTone {
  return status === 'DONE' ? 'success' : status === 'RUNNING' || status === 'QUEUED' ? 'warning' : 'neutral';
}

export function awardStatusTone(status: AwardCampaignView['status']): BroadcastTone {
  return status === 'ACTIVE' ? 'success' : status === 'PROPOSED' ? 'warning' : status === 'REJECTED' ? 'danger' : 'neutral';
}

// ---- the delivery progress -------------------------------------------------------------------------------------------

export interface ProgressShare {
  readonly total: number;
  readonly sent: number;
  /** Skipped (no consent, no address, not active, cancelled) — never a failure of the platform. */
  readonly skipped: number;
  readonly failed: number;
  /** Waiting: PENDING (including the ones deferred by quiet hours) and CLAIMED. */
  readonly waiting: number;
  /** 0..1 of the recipients that reached a final status. */
  readonly settled: number;
}

export function progressShare(progress: BroadcastProgressView): ProgressShare {
  const n = (s: string): number => progress.byStatus[s] ?? 0;
  const total = progress.total;
  const sent = n('SENT');
  const skipped = n('SKIPPED');
  const failed = n('FAILED');
  const waiting = n('PENDING') + n('CLAIMED');
  return { total, sent, skipped, failed, waiting, settled: total === 0 ? 0 : (sent + skipped + failed) / total };
}

/** The deferred recipients (waiting for the quiet window to close) among the rows of a progress report. */
export function deferredCount(progress: BroadcastProgressView): number {
  return progress.rows.filter((r) => r.status === 'PENDING' && r.reasonCode === 'BROADCAST_QUIET_HOURS_DEFERRED').reduce((n, r) => n + r.count, 0);
}

// ---- refusals --------------------------------------------------------------------------------------------------------

// ---- the drafts ------------------------------------------------------------------------------------------------------

/** A comma- or line-separated list typed by the operator, trimmed and de-duplicated, empty entries dropped. */
export function parseList(raw: string): readonly string[] {
  return [...new Set(raw.split(/[\n,;]/).map((s) => s.trim()).filter((s) => s !== ''))];
}

export function describeBannerScope(scope: BannerScopeView): { readonly kind: BannerScopeView['kind']; readonly ref: string } {
  switch (scope.kind) {
    case 'PLATFORM': return { kind: scope.kind, ref: '' };
    case 'COUNTRY': return { kind: scope.kind, ref: scope.country ?? '' };
    case 'REGION': return { kind: scope.kind, ref: `${scope.country ?? ''} / ${scope.region ?? ''}` };
    case 'MARKET': return { kind: scope.kind, ref: scope.marketId ?? '' };
  }
}

/** Why the signed-in operator cannot activate an award campaign (undefined → may). */
export function awardDecideBlock(campaign: AwardCampaignView, operatorId: string | undefined, mayManage: boolean, selfApproval = false): 'NO_PERMISSION' | 'NOT_PENDING' | 'OWN_PROPOSAL' | undefined {
  if (!mayManage) return 'NO_PERMISSION';
  if (campaign.status !== 'PROPOSED') return 'NOT_PENDING';
  if (!selfApproval && operatorId !== undefined && campaign.proposedBy === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

/** How much of the campaign budget has been paid, 0..1 (a budget of zero reads as fully paid). */
export function budgetUsed(campaign: AwardCampaignView): number {
  const total = BigInt(campaign.totalCap.amountMinor);
  if (total <= 0n) return 1;
  const used = (BigInt(campaign.spent.amountMinor) * 10_000n) / total;
  return Math.min(1, Number(used) / 10_000);
}
